/**
 * Roles & capability model — pure, I/O-free, shared by web and api.
 *
 * Governance for an *advisory* tool: roles decide who may capture, respond to
 * insights, export a handover, or conduct a post-incident review. They never
 * change what the deterministic engine considers true — they only gate actions
 * in the UI/API. "Unknown role" is first-class: an unauthenticated/unknown
 * principal maps to `observer`, who can read but not act.
 */

/** Officer roles, least → most privileged (excluding the read-only observer). */
export const OFFICER_ROLES = [
  'crew-leader',
  'duty-officer',
  'incident-controller',
  'reviewer-sme',
  'admin',
] as const;
export type OfficerRole = (typeof OFFICER_ROLES)[number];

/** Read-only fallback for an unauthenticated or unmapped principal. */
export const OBSERVER_ROLE = 'observer' as const;
export type Role = OfficerRole | typeof OBSERVER_ROLE;

export const ROLE_LABELS: Record<Role, string> = {
  observer: 'Observer',
  'crew-leader': 'Crew leader',
  'duty-officer': 'Duty officer',
  'incident-controller': 'Incident controller',
  'reviewer-sme': 'Reviewer / SME',
  admin: 'Administrator',
};

/** One-line description of what each role is for (shown in the role picker). */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  observer: 'Read-only view of the incident, insights and sources.',
  'crew-leader': 'Captures the field picture (ITASC) and responds to insights.',
  'duty-officer': 'Runs the incident: capture, responds, and exports handovers.',
  'incident-controller': 'Command oversight with full operational control.',
  'reviewer-sme': 'Conducts post-incident review and rates insight usefulness.',
  admin: 'Full access including configuration and all reviews.',
};

/**
 * Capabilities are the gated *actions* in the app. Reading is never gated —
 * the tool is advisory and transparency is a feature.
 */
export const CAPABILITIES = [
  /** Create or load an incident. */
  'incident.create',
  /** Edit the live picture: ITASC, stage, resources, water plan. */
  'incident.edit',
  /** Acknowledge or dismiss an insight (with a recorded reason). */
  'insight.respond',
  /** Generate / copy a SITREP, CAD note or handover export. */
  'export.generate',
  /** Conduct the post-incident review and submit insight-usefulness feedback. */
  'review.conduct',
  /** Administer configuration / thresholds. */
  'config.manage',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

const FIELD: Capability[] = ['incident.create', 'incident.edit', 'insight.respond', 'export.generate'];

/** What each role may do. Higher roles are supersets, but stated explicitly. */
export const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  observer: [],
  'crew-leader': ['incident.edit', 'insight.respond'],
  'duty-officer': FIELD,
  'incident-controller': FIELD,
  'reviewer-sme': ['export.generate', 'review.conduct'],
  admin: [...CAPABILITIES],
};

/** True when `role` is permitted to perform `capability`. */
export function can(role: Role, capability: Capability): boolean {
  return ROLE_CAPABILITIES[role]?.includes(capability) ?? false;
}

/** The actor string recorded in the audit log for a role. */
export function actorForRole(role: Role): string {
  return role === OBSERVER_ROLE ? 'observer' : role;
}

export const isOfficerRole = (r: string): r is OfficerRole =>
  (OFFICER_ROLES as readonly string[]).includes(r);

/**
 * The Azure Static Web Apps client principal (`/.auth/me`). SWA injects roles
 * from its auth config; we map the highest app role present, else `observer`.
 * The `authenticated`/`anonymous` built-in roles are ignored for our mapping.
 */
export interface ClientPrincipal {
  identityProvider?: string;
  userId?: string;
  userDetails?: string;
  userRoles?: string[];
}

/** Map a SWA principal's roles to our single effective role (highest wins). */
export function roleFromPrincipal(principal: ClientPrincipal | null | undefined): Role {
  const roles = principal?.userRoles ?? [];
  // Highest-privilege app role present wins; built-in roles are not app roles.
  for (let i = OFFICER_ROLES.length - 1; i >= 0; i -= 1) {
    if (roles.includes(OFFICER_ROLES[i]!)) return OFFICER_ROLES[i]!;
  }
  return OBSERVER_ROLE;
}
